import React, { useState, useEffect, useMemo } from 'react';
import { callApi } from '../api/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const AdminSales = ({ showMessage }) => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Date range filters. Default to last 30 days.
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    });
    const [endDate, setEndDate] = useState(() => {
        return new Date();
    });

    useEffect(() => {
        const fetchSales = async () => {
            setIsLoading(true);
            try {
                // Fetch all orders
                const data = await callApi('/admin/orders');
                
                // Keep "Delivered" orders OR manual bills that were previously set to "Payment Verification Pending"
                const completedOrders = data.filter(order => order.status === 'Delivered' || (order.isManualBill && order.status === 'Payment Verification Pending'));
                setOrders(completedOrders);
            } catch (error) {
                console.error("Failed to fetch sales data", error);
                showMessage('Failed to fetch sales data.', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchSales();
    }, [showMessage]);

    // Calculate metrics and filter by date
    const { filteredSales, totalRevenue, totalOrders } = useMemo(() => {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        let revenue = 0;
        let count = 0;

        const filtered = orders.filter(order => {
            const rawDate = order.orderDate || order.createdAt;
            const orderDate = new Date(rawDate);
            const inRange = orderDate >= start && orderDate <= end;
            
            if (inRange) {
                revenue += (order.total || 0);
                count += 1;
            }
            
            return inRange;
        });

        // Sort filtered sales by date descending (newest first)
        filtered.sort((a, b) => new Date(b.orderDate || b.createdAt) - new Date(a.orderDate || a.createdAt));

        return { filteredSales: filtered, totalRevenue: revenue, totalOrders: count };
    }, [orders, startDate, endDate]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                <i className="fas fa-circle-notch fa-spin text-4xl text-sky-500 mb-4"></i>
                <p className="text-slate-400 font-medium">Loading sales data...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-2">
                <div>
                    <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800">Sales Report</h2>
                    <p className="text-xs text-slate-500 font-medium mt-0.5">Track revenue and completed orders</p>
                </div>
            </div>

            {/* Date Filters */}
            <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex flex-wrap gap-4 items-end">
                <div>
                    <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Start Date</label>
                    <DatePicker 
                        selected={startDate} 
                        onChange={date => {
                            setStartDate(date);
                            setCurrentPage(1);
                        }}
                        dateFormat="dd/MM/yyyy"
                        className="border-slate-200 rounded-xl px-3 py-1.5 bg-slate-50 focus:ring-2 focus:ring-sky-400 focus:border-transparent outline-none transition-all text-sm font-semibold w-[140px]"
                    />
                </div>
                <div>
                    <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">End Date</label>
                    <DatePicker 
                        selected={endDate} 
                        onChange={date => {
                            setEndDate(date);
                            setCurrentPage(1);
                        }}
                        dateFormat="dd/MM/yyyy"
                        className="border-slate-200 rounded-xl px-3 py-1.5 bg-slate-50 focus:ring-2 focus:ring-sky-400 focus:border-transparent outline-none transition-all text-sm font-semibold w-[140px]"
                    />
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-emerald-400 to-teal-500 p-6 rounded-2xl shadow-lg shadow-emerald-500/30 text-white relative overflow-hidden">
                    <i className="fas fa-indian-rupee-sign absolute -right-4 -bottom-4 text-7xl opacity-10 transform -rotate-12"></i>
                    <div className="relative z-10 flex justify-between items-start mb-2">
                        <p className="text-sm font-bold uppercase tracking-wider opacity-90">Total Revenue</p>
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                            <i className="fas fa-chart-line text-lg"></i>
                        </div>
                    </div>
                    <p className="text-3xl sm:text-4xl font-black tracking-tight relative z-10">₹{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>

                <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-6 rounded-2xl shadow-lg shadow-blue-500/30 text-white relative overflow-hidden">
                    <i className="fas fa-check-circle absolute -right-4 -bottom-4 text-7xl opacity-10 transform -rotate-12"></i>
                    <div className="relative z-10 flex justify-between items-start mb-2">
                        <p className="text-sm font-bold uppercase tracking-wider opacity-90">Completed Orders</p>
                        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                            <i className="fas fa-box-open text-lg"></i>
                        </div>
                    </div>
                    <p className="text-3xl sm:text-4xl font-black tracking-tight relative z-10">{totalOrders.toLocaleString()}</p>
                </div>
            </div>

            {/* Sales Table */}
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden mt-6">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 border-b border-slate-100">
                            <tr>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Order ID</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Customer</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Payment Method</th>
                                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {filteredSales.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-slate-400 font-medium">
                                        No sales found in this date range.
                                    </td>
                                </tr>
                            ) : (
                                filteredSales.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(order => (
                                    <tr key={order._id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-medium">
                                            {(order.orderDate || order.createdAt) ? new Date(order.orderDate || order.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="font-mono text-sm font-bold text-slate-700 bg-slate-100 px-2 py-1 rounded-md">{order.orderId}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="font-bold text-slate-800 text-sm">{order.customerName || order.shippingAddress?.name || 'Guest User'}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                                                order.paymentMethod === 'Cash on Delivery' || order.paymentMethod === 'COD' 
                                                ? 'bg-amber-100 text-amber-800' 
                                                : 'bg-emerald-100 text-emerald-800'
                                            }`}>
                                                {order.paymentMethod || 'Online'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-black text-slate-800">
                                            ₹{(order.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                {Math.ceil(filteredSales.length / itemsPerPage) > 1 && (
                    <div className="flex justify-between items-center p-4 border-t border-slate-100 bg-slate-50">
                        <button 
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                            disabled={currentPage === 1}
                            className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        >
                            Previous
                        </button>
                        <span className="text-xs font-bold text-slate-500">
                            Page {currentPage} of {Math.ceil(filteredSales.length / itemsPerPage)}
                        </span>
                        <button 
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredSales.length / itemsPerPage)))} 
                            disabled={currentPage === Math.ceil(filteredSales.length / itemsPerPage)}
                            className="bg-white border border-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-bold shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSales;
